/* eslint-disable no-nested-ternary */
/* eslint-disable radix */
/* eslint-disable no-plusplus */
import { ReactElement, useEffect, useMemo, useState } from 'react';

import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
} from 'recharts';

import { useLoading } from '~/hooks/loading';
import useQuery from '~/hooks/useQuery';
import {
  IFinancesData,
  IVindiPaymentByMonthAndYearData,
  IVindiPaymentMethodsData,
  IVindiPaymentStatusData,
  IVindiReportsFinancesData,
} from '~/models/Boss';
import ExplainDate from '~/pages/Billing/ExplainDate';
import { IBossService } from '~/services';
import {
  filterVindiBillsData,
  getPaymentStatus,
  getPaymentType,
} from '~/utils/financeReports';
import { abbreviatedMonths } from '~/utils/months';

import * as S from './styles';

function FinanceCharts(): ReactElement {
  const { setLoading } = useLoading();
  const query = useQuery();
  const [trainersData, setTrainerData] = useState<IFinancesData>();

  const [
    countSubscriptionsByPlanName,
    setCountSubscriptionsByPlanName,
  ] = useState<IVindiReportsFinancesData[]>([]);

  const [
    countSubscriptionsByPaymentMethod,
    setCountSubscriptionsByPaymentMethod,
  ] = useState<IVindiPaymentMethodsData[]>([]);

  const [countSubscriptionsByStatus, setCountSubscriptionsByStatus] = useState<
    IVindiPaymentStatusData[]
  >([]);

  const [paymentsByMonthAndYear, setPaymentsByMonthAndYear] = useState<
    IVindiPaymentByMonthAndYearData[]
  >([]);

  const queryValue = useMemo(() => {
    const q = new URLSearchParams();
    const initial = query.get('initialDate');
    const last = query.get('lastDate');

    if (initial) q.append('start', initial);
    if (last) q.append('end', last);

    return q;
  }, [query]);

  const getPaymentType = (paymentMethod: string) => {
    const payment = {
      debit_card: 'Cartão de débito',
      billet: 'Boleto',
      cash: 'Dinheiro',
      default: 'Não identificado',
      credit_card: 'Cartão de crédito',
      transfer: 'Transferência',
      others: 'Outros',
    };

    if (!payment[paymentMethod as keyof typeof payment]) {
      return 'não informado';
    }

    return payment[paymentMethod as keyof typeof payment];
  };

  const getPaymentStatus = (paymentStatus: string) => {
    const status = {
      pending: 'Pendente',
      overdue: 'Atrasado',
      paid: 'Pago',
      canceled: 'Cancelado',
    };

    if (!status[paymentStatus as keyof typeof status]) return 'outros';

    return status[paymentStatus as keyof typeof status];
  };

  const handleGetReportTrainers = async () => {
    try {
      setLoading(true);

      let vindiData = null;

      let vindPaymentStatus = null;
      let vindReportsFinances = null;
      let vindPaymentMethods = null;
      let vindPaymentByMonthAndYear = null;

      if (queryValue.toString().includes('end=')) {
        vindiData = await IBossService.newGetReportsFinances(
          queryValue.toString(),
        );

        // ? New
        vindPaymentStatus = await IBossService.getVindiPaymentStatus(
          queryValue.toString(),
        );
        vindReportsFinances = await IBossService.getVindiReportsFinances(
          queryValue.toString(),
        );
        vindPaymentMethods = await IBossService.getVindiPaymentMethods(
          queryValue.toString(),
        );
        vindPaymentByMonthAndYear = await IBossService.getVindiPaymentByMonthAndYear(
          queryValue.toString(),
        );
      } else {
        const today = new Date().toISOString().split('T')[0];

        vindiData = await IBossService.newGetReportsFinances(
          `${queryValue.toString()}&end=${today}`,
        );

        // ? New
        vindPaymentStatus = await IBossService.getVindiPaymentStatus(
          `${queryValue.toString()}&end=${today}`,
        );
        vindReportsFinances = await IBossService.getVindiReportsFinances(
          `${queryValue.toString()}&end=${today}`,
        );
        vindPaymentMethods = await IBossService.getVindiPaymentMethods(
          `${queryValue.toString()}&end=${today}`,
        );
        vindPaymentByMonthAndYear = await IBossService.getVindiPaymentByMonthAndYear(
          `${queryValue.toString()}&end=${today}`,
        );
      }

      vindPaymentStatus.data.forEach((paymentStatus) => {
        // eslint-disable-next-line no-param-reassign
        paymentStatus.paymentStatus = getPaymentStatus(
          paymentStatus.paymentStatus,
        );
      });

      setCountSubscriptionsByStatus(vindPaymentStatus.data);
      setCountSubscriptionsByPlanName(vindReportsFinances.data);
      setCountSubscriptionsByPaymentMethod(vindPaymentMethods.data);
      setPaymentsByMonthAndYear(vindPaymentByMonthAndYear.data);

      const { data } = await IBossService.getReportsFinances(
        queryValue.toString(),
      );

      setTrainerData(data);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    handleGetReportTrainers();
  }, []);

  return (
    <S.Container>
      <ExplainDate
        initial={query.get('initialDate') || ''}
        last={query.get('lastDate') || ''}
      />
      {trainersData && (
        <>
          <S.ChartContainer>
            <S.ChartTitle>Inscrições pelo nome do plano</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={countSubscriptionsByPlanName}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="planName" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                itemStyle={{ color: '#000000' }}
              />
              <Legend />
              <Bar dataKey="count" name="inscrições" fill="#FFDF22" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Inscrições pelo método de pagamento</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={countSubscriptionsByPaymentMethod}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="paymentMethod" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                itemStyle={{ color: '#000000' }}
              />
              <Legend />
              <Bar dataKey="count" name="inscrições" fill="#FFDF22" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Inscrições pelo status de fatura</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={countSubscriptionsByStatus}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="paymentStatus" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                itemStyle={{ color: '#000000' }}
              />
              <Legend />
              <Bar dataKey="count" name="inscrições" fill="#FFDF22" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Pagamentos por mês e ano</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={paymentsByMonthAndYear}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mouth" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                itemStyle={{ color: '#000000' }}
                formatter={(value: string) => `R$ ${value}`}
              />
              <Legend />
              <Bar dataKey="total" name="Total" fill="#FFDF22" />
            </BarChart>
          </S.ChartContainer>
        </>
      )}
    </S.Container>
  );
}

export default FinanceCharts;
